import React, { useEffect, useMemo } from 'react';
import { components } from 'react-select';
import { Controller, useWatch } from 'react-hook-form';
import { inputSelectCustomStyles, StyledSelect } from '../../../../../components/CustomSearchBar';
import { ErrorText } from '../../../../../components/utils/AvatarCropper';
import { Palette } from '../../../../../components/utils';
import { FieldBlock, FieldInnerFull, FieldLabel } from '../../../../../components/WizardV2/wizardStylesV2';
import { FieldRest, RateTypeSelectPlaceholder, WizardTextInput } from './addAmenityWizardStylesV2';
import {
  AMENITY_COST_TYPE,
  getRateAmountLabel,
  getRateTypeOptions,
  isUnsetCostType,
  isValidCostTypeForBooking
} from './amenityPaymentConfigV2';

function sanitizeDecimalInput(raw) {
  if (raw == null) return '';
  const str = String(raw).replace(/[^\d.]/g, '');
  const parts = str.split('.');
  if (parts.length <= 1) return str;
  return `${parts[0]}.${parts.slice(1).join('').slice(0, 2)}`;
}

function PaymentAmountInput({ control, name, placeholder, id }) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, onBlur, value, ref } }) => (
        <WizardTextInput
          ref={ref}
          type="text"
          inputMode="decimal"
          id={id}
          placeholder={placeholder}
          autoComplete="off"
          onBlur={onBlur}
          onChange={(e) => {
            const cleaned = sanitizeDecimalInput(e.target.value);
            onChange(cleaned === '' ? null : cleaned);
          }}
          value={value != null && value !== '' ? String(value) : ''}
        />
      )}
    />
  );
}

function RateTypePlaceholder(props) {
  return (
    <components.Placeholder {...props}>
      <RateTypeSelectPlaceholder>{props.children}</RateTypeSelectPlaceholder>
    </components.Placeholder>
  );
}

function AddAmenityPaymentStepV2({ t, control, errors, setValue }) {
  const td = (key, fallback) => t(key, { defaultValue: fallback });
  const allowMultiDayWatch = useWatch({ control, name: 'allow_multi_day' });
  const costTypeWatch = useWatch({ control, name: 'cost_type' });
  const rateTypePlaceholder = td('add_amenity_rate_type_placeholder', 'Select rate type');

  const rateTypeOptions = useMemo(
    () => getRateTypeOptions(allowMultiDayWatch, t),
    [allowMultiDayWatch, t]
  );

  const rateAmountLabel = useMemo(
    () => getRateAmountLabel(costTypeWatch, t),
    [costTypeWatch, t]
  );

  const rateTypeSelectStyles = useMemo(() => {
    const base = inputSelectCustomStyles({ minHeight: '56px', height: 'auto' });
    const withPlaceholder = {
      ...base,
      placeholder: (styles) => ({
        ...styles,
        color: Palette.BLACK_50,
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '24px',
        margin: 0
      })
    };
    if (!errors?.cost_type) return withPlaceholder;
    return {
      ...withPlaceholder,
      control: (styles, state) => ({
        ...base.control(styles, state),
        border: `2px solid ${Palette.ERROR}`
      })
    };
  }, [errors?.cost_type]);

  useEffect(() => {
    if (!setValue) return;
    if (
      !allowMultiDayWatch &&
      Number(costTypeWatch) === AMENITY_COST_TYPE.DAILY
    ) {
      setValue('cost_type', AMENITY_COST_TYPE.NONE, {
        shouldDirty: true,
        shouldValidate: false
      });
    }
  }, [allowMultiDayWatch, costTypeWatch, setValue]);

  useEffect(() => {
    if (!setValue) return;
    if (isUnsetCostType(costTypeWatch)) return;
    if (!isValidCostTypeForBooking(costTypeWatch, allowMultiDayWatch)) {
      setValue('cost_type', AMENITY_COST_TYPE.NONE, {
        shouldDirty: true,
        shouldValidate: false
      });
    }
  }, [allowMultiDayWatch, costTypeWatch, rateTypeOptions, setValue]);

  return (
    <>
      <FieldBlock>
        <FieldInnerFull>
          <FieldLabel>{td('add_amenity_rate_type_label', 'Rate Type')}</FieldLabel>
          <Controller
            control={control}
            name="cost_type"
            render={({ field: { onChange, onBlur, value, ref } }) => {
              const numeric =
                value != null && value !== '' ? Number(value) : AMENITY_COST_TYPE.NONE;
              const selected = isValidCostTypeForBooking(numeric, allowMultiDayWatch)
                ? rateTypeOptions.find((o) => o.value === numeric) ?? null
                : null;

              return (
                <StyledSelect
                  ref={ref}
                  inputId="wizard-payment-cost_type"
                  instanceId="amenity-payment-cost-type"
                  options={rateTypeOptions}
                  value={selected}
                  placeholder={rateTypePlaceholder}
                  onChange={(opt) =>
                    onChange(opt != null ? opt.value : AMENITY_COST_TYPE.NONE)
                  }
                  onBlur={onBlur}
                  styles={rateTypeSelectStyles}
                  components={{ Placeholder: RateTypePlaceholder }}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isClearable={false}
                  isSearchable={false}
                  menuPlacement="auto"
                />
              );
            }}
          />
        </FieldInnerFull>
        {errors?.cost_type && <ErrorText>{errors.cost_type.message}</ErrorText>}
      </FieldBlock>

      <FieldBlock>
        <FieldInnerFull>
          <FieldLabel>{rateAmountLabel}</FieldLabel>
          <FieldRest>
            <PaymentAmountInput
              control={control}
              name="payment_rate"
              id="wizard-payment_rate"
              placeholder={td(
                'add_amenity_payment_rate_placeholder',
                'Enter amount with tax included'
              )}
            />
          </FieldRest>
        </FieldInnerFull>
        {errors?.payment_rate && <ErrorText>{errors.payment_rate.message}</ErrorText>}
      </FieldBlock>

      <FieldBlock>
        <FieldInnerFull>
          <FieldLabel>
            {td('add_amenity_service_fee_label', 'Service Fee (optional)')}
          </FieldLabel>
          <FieldRest>
            <PaymentAmountInput
              control={control}
              name="service_charge"
              id="wizard-service_charge"
              placeholder={td('add_amenity_payment_optional_placeholder', 'Enter amount')}
            />
          </FieldRest>
        </FieldInnerFull>
        {errors?.service_charge && <ErrorText>{errors.service_charge.message}</ErrorText>}
      </FieldBlock>

      <FieldBlock>
        <FieldInnerFull>
          <FieldLabel>{td('add_amenity_deposit_label', 'Deposit (optional)')}</FieldLabel>
          <FieldRest>
            <PaymentAmountInput
              control={control}
              name="deposit"
              id="wizard-deposit"
              placeholder={td('add_amenity_payment_optional_placeholder', 'Enter amount')}
            />
          </FieldRest>
        </FieldInnerFull>
        {errors?.deposit && <ErrorText>{errors.deposit.message}</ErrorText>}
      </FieldBlock>
    </>
  );
}

export default AddAmenityPaymentStepV2;
